import { useState } from 'react';
import { Mic, MicOff, Phone, PhoneOff, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useVoiceFAQChatbot } from '@/hooks/useVoiceFAQChatbot';
import { cn } from '@/lib/utils';
import chatbotLogo from "@/assets/lexique-chatbot-logo-blue.png";

interface VoiceFAQChatbotProps {
  onClose?: () => void;
}

export const VoiceFAQChatbot = ({ onClose }: VoiceFAQChatbotProps) => {
  const [isStarting, setIsStarting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  
  const {
    isConnected,
    isSpeaking,
    status,
    startConversation,
    endConversation,
    setVolume,
  } = useVoiceFAQChatbot();

  const handleStart = async () => {
    setIsStarting(true);
    try {
      await startConversation();
    } finally {
      setIsStarting(false);
    }
  };

  const handleEnd = async () => {
    await endConversation();
    onClose?.();
  };

  const handleMuteToggle = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    setVolume(newMuted ? 0 : 1);
  };

  return (
    <Card className="w-full max-w-md mx-auto p-6 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950 dark:to-background">
      <div className="flex flex-col items-center gap-6">
        {/* Logo et titre */}
        <div className="flex items-center gap-3">
          <img src={chatbotLogo} alt="QQOQ" className="w-16 h-16" />
          <div>
            <h2 className="text-xl font-bold text-blue-600 dark:text-blue-400">
              Assistant Vocal QQOQ
            </h2>
            <p className="text-sm text-muted-foreground">
              Posez vos questions à haute voix
            </p>
          </div>
        </div>

        {/* Indicateur de statut */}
        <div className={cn(
          "w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300",
          isConnected 
            ? isSpeaking 
              ? "bg-green-500/20 animate-pulse ring-4 ring-green-500/50" 
              : "bg-blue-500/20 ring-4 ring-blue-500/50"
            : "bg-gray-200 dark:bg-gray-800"
        )}>
          <div className={cn(
            "w-24 h-24 rounded-full flex items-center justify-center transition-all",
            isConnected 
              ? isSpeaking 
                ? "bg-green-500" 
                : "bg-blue-500"
              : "bg-gray-400 dark:bg-gray-600"
          )}>
            {isStarting ? (
              <Loader2 className="w-10 h-10 text-white animate-spin" />
            ) : isConnected ? (
              isSpeaking ? (
                <Volume2 className="w-10 h-10 text-white animate-pulse" />
              ) : (
                <Mic className="w-10 h-10 text-white" />
              )
            ) : (
              <MicOff className="w-10 h-10 text-white" />
            )}
          </div>
        </div>

        {/* Statut textuel */}
        <p className={cn(
          "text-sm font-medium",
          isConnected 
            ? isSpeaking 
              ? "text-green-600 dark:text-green-400" 
              : "text-blue-600 dark:text-blue-400"
            : "text-muted-foreground"
        )}>
          {isStarting ? "Connexion en cours..." :
           isConnected 
            ? isSpeaking 
              ? "🗣️ L'assistant parle..." 
              : "🎤 Je vous écoute..."
            : "Cliquez pour démarrer"}
        </p>

        {/* Boutons de contrôle */}
        <div className="flex gap-3">
          {!isConnected ? (
            <Button
              size="lg"
              onClick={handleStart}
              disabled={isStarting}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            >
              {isStarting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Phone className="w-5 h-5" />
              )}
              Démarrer la conversation
            </Button>
          ) : (
            <>
              <Button
                size="lg"
                variant="outline"
                onClick={handleMuteToggle}
                className={cn(
                  "gap-2",
                  isMuted && "bg-orange-100 border-orange-300 dark:bg-orange-900/30"
                )}
              >
                {isMuted ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
                {isMuted ? "Son coupé" : "Couper le son"}
              </Button>
              
              <Button
                size="lg"
                variant="destructive"
                onClick={handleEnd}
                className="gap-2"
              >
                <PhoneOff className="w-5 h-5" />
                Terminer
              </Button>
            </>
          )}
        </div>

        {/* Instructions */}
        {isConnected && (
          <div className="text-center text-sm text-muted-foreground mt-4 space-y-1">
            <p>💡 Parlez naturellement, l'assistant vous écoute</p>
            <p>📚 Posez des questions sur notre plateforme et nos services</p>
          </div>
        )}
      </div>
    </Card>
  );
};
